/**
 * Dialog "Salvar N na minha biblioteca" — clona vários templates do catálogo
 * (Explorar) de uma vez pra uma pasta/projeto.
 *
 * Usa o conteúdo já carregado dos cards (TemplateLibraryItem) e cria tudo via
 * bulk, marcando cada um como clonado (source_template_intel_id).
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, BookmarkPlus } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import {
    getProjects, getFolderTree, bulkCreateCustomTemplates, createFolder,
} from '@/services/api';
import type { TemplateLibraryItem, CustomTemplateBulkItem } from '@/types/template-library';
import { flattenFolders } from './FolderTree';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Templates do catálogo selecionados. */
    templates: TemplateLibraryItem[];
    onSaved?: () => void;
}

export default function BulkCloneToLibraryDialog({ open, onOpenChange, templates, onSaved }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [projectId, setProjectId] = useState<number | null>(null);
    const [folderId, setFolderId] = useState<number | null>(null);

    useEffect(() => {
        if (open) { setFolderId(null); }
    }, [open]);

    const { data: projects = [] } = useQuery({
        queryKey: ['template-library-projects'],
        queryFn: getProjects,
        staleTime: 1000 * 60 * 5,
        enabled: open,
    });

    const { data: tree } = useQuery({
        queryKey: ['folder-tree', projectId],
        queryFn: () => getFolderTree(projectId),
        enabled: open,
        staleTime: 1000 * 15,
    });
    const flatFolders = flattenFolders(tree?.folders ?? []);

    const mutation = useMutation({
        mutationFn: () => {
            const items: CustomTemplateBulkItem[] = templates.map((t) => ({
                name: t.title || `Template ${t.template_id}`,
                template_type: t.template_type,
                title: t.title || undefined,
                subtitle: t.subtitle || undefined,
                image_url: t.image_url || undefined,
                text_content: t.text_content || undefined,
                buttons: (t.buttons_json || []).map((b) => ({
                    title: b.title || '', url: b.url || '',
                })),
                source_template_intel_id: t.template_id,
            }));
            return bulkCreateCustomTemplates({
                project_id: projectId, folder_id: folderId, items,
            });
        },
        onSuccess: (r) => {
            toast({
                title: 'Salvos na biblioteca',
                description: `${r.created} ${r.created === 1 ? 'modelo clonado' : 'modelos clonados'} pra "Meus templates".`,
            });
            queryClient.invalidateQueries({ queryKey: ['custom-templates'] });
            queryClient.invalidateQueries({ queryKey: ['folder-tree'] });
            onSaved?.();
            onOpenChange(false);
        },
        onError: (e: any) => toast({
            title: 'Falha ao salvar',
            description: e?.response?.data?.detail || e?.message, variant: 'destructive',
        }),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <BookmarkPlus className="w-5 h-5 text-violet-600" />
                        Salvar {templates.length} {templates.length === 1 ? 'modelo' : 'modelos'} na biblioteca
                    </DialogTitle>
                    <DialogDescription>
                        Cria cópias editáveis em "Meus templates". Cada um usa o próprio título como nome.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label>Projeto</Label>
                        <SearchableSelect
                            value={projectId == null ? '' : String(projectId)}
                            onValueChange={(v) => { setProjectId(v ? Number(v) : null); setFolderId(null); }}
                            options={[
                                { value: '', label: 'Pessoal (sem projeto)' },
                                ...projects.map((p) => ({ value: String(p.id), label: p.name })),
                            ]}
                            placeholder="Selecione o projeto"
                            searchPlaceholder="Buscar projeto..."
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Pasta</Label>
                        <SearchableSelect
                            value={folderId == null ? '' : String(folderId)}
                            onValueChange={(v) => setFolderId(v ? Number(v) : null)}
                            options={[
                                { value: '', label: 'Raiz (sem pasta)' },
                                ...flatFolders.map((f) => ({
                                    value: String(f.id),
                                    label: `${'— '.repeat(f.depth)}${f.name}`,
                                })),
                            ]}
                            placeholder="Raiz (sem pasta)"
                            searchPlaceholder="Buscar ou criar pasta..."
                            createLabel="Criar pasta"
                            onCreate={async (name) => {
                                const r = await createFolder({ name, project_id: projectId, parent_id: null });
                                await queryClient.invalidateQueries({ queryKey: ['folder-tree', projectId] });
                                return { value: String(r.id ?? ''), label: name };
                            }}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
                        Cancelar
                    </Button>
                    <Button onClick={() => mutation.mutate()} disabled={templates.length === 0 || mutation.isPending}>
                        {mutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                        Salvar {templates.length}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
